import React, { useState } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  InputNumber, Select, Switch, message,
  Statistic, Tag, Tabs, Descriptions, Space
} from 'antd';
import {
  EditOutlined, SaveOutlined,
  SettingOutlined, DollarOutlined,
  CarOutlined, ShoppingOutlined,
} from '@ant-design/icons';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { Option } = Select;
const { TabPane } = Tabs;

// ── Initial Commission Config ──
const INITIAL_CONFIG = {
  products: {
    economy: { rate: 8, min: 5, max: 15 },
    premium: { rate: 10, min: 5, max: 18 },
    luxury: { rate: 12, min: 8, max: 20 },
    super_luxury: { rate: 14, min: 10, max: 22 },
  },
  b2b: { rate: 6, min: 3, max: 12 },
  realestate: { rate: 1.5, min: 1, max: 3 },
  leads: {
    civil: 499, painting: 299, plumbing: 299,
    electrical: 299, carpentry: 399, tiles: 299,
    hvac: 499, fabrication: 399, interior: 499,
    repair: 199, outdoor: 299, industrial: 599,
  },
};

// ── Delivery Fee Config ──
const INITIAL_DELIVERY = {
  light: { base: 40, perKm: 8, freeAbove: 2000 },
  heavy: { base: 200, perKm: 15, weightSurcharge: 50 },
  truck: { base: 800, perKm: 20, weightSurcharge: 30 },
};

// ── Subscription Plans ──
const INITIAL_PLANS = [
  { id: 'dealer_basic', name: 'Dealer Basic', price: 0, products: 10, analytics: false, featured: false },
  { id: 'dealer_standard', name: 'Dealer Standard', price: 999, products: 50, analytics: true, featured: false },
  { id: 'dealer_premium', name: 'Dealer Premium', price: 2499, products: 999, analytics: true, featured: true },
  { id: 'builder_basic', name: 'Builder Basic', price: 0, products: 1, analytics: false, featured: false },
  { id: 'builder_standard', name: 'Builder Standard', price: 1499, products: 5, analytics: true, featured: false },
  { id: 'builder_premium', name: 'Builder Premium', price: 3999, products: 999, analytics: true, featured: true },
  { id: 'supplier_b2b', name: 'Supplier B2B', price: 1999, products: 999, analytics: true, featured: false },
];

const TIER_COLORS = {
  economy: '#52C41A', premium: '#1890FF',
  luxury: '#C9A84C', super_luxury: '#722ED1',
};

export default function CommissionConfig() {
  const [config, setConfig] = useState(INITIAL_CONFIG);
  const [delivery, setDelivery] = useState(INITIAL_DELIVERY);
  const [plans, setPlans] = useState(INITIAL_PLANS);
  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editType, setEditType] = useState('');
  const [tempValue, setTempValue] = useState({});
  const [loading, setLoading] = useState(false);

  const saveAuditLog = async (action, details) => {
    await addDoc(collection(db, 'auditLogs'), {
      action, details, adminEmail: 'admin',
      timestamp: serverTimestamp(),
    });
  };

  const openEdit = (type, key, data) => {
    setEditType(type);
    setEditItem(key);
    setTempValue({ ...data });
    setEditModal(true);
  };

  const saveEdit = async () => {
    setLoading(true);
    try {
      if (editType === 'product_tier') {
        const old = config.products[editItem];
        setConfig(prev => ({
          ...prev,
          products: { ...prev.products, [editItem]: tempValue },
        }));
        await saveAuditLog('COMMISSION UPDATED',
          'Product tier: ' + editItem + ' | Rate: ' + old.rate + '% -> ' + tempValue.rate + '%');
      } else if (editType === 'b2b') {
        setConfig(prev => ({ ...prev, b2b: tempValue }));
        await saveAuditLog('COMMISSION UPDATED', 'B2B rate updated to ' + tempValue.rate + '%');
      } else if (editType === 'realestate') {
        setConfig(prev => ({ ...prev, realestate: tempValue }));
        await saveAuditLog('COMMISSION UPDATED', 'Real Estate rate updated to ' + tempValue.rate + '%');
      } else if (editType === 'delivery') {
        setDelivery(prev => ({ ...prev, [editItem]: tempValue }));
        await saveAuditLog('DELIVERY FEE UPDATED', 'Vehicle type: ' + editItem);
      } else if (editType === 'lead') {
        setConfig(prev => ({
          ...prev,
          leads: { ...prev.leads, [editItem]: tempValue.price },
        }));
        await saveAuditLog('LEAD PRICE UPDATED',
          'Category: ' + editItem + ' | Price: ₹' + tempValue.price);
      }
      message.success('Configuration saved successfully');
      setEditModal(false);
    } catch (error) {
      message.error('Save failed. Try again.');
    }
    setLoading(false);
  };

  // Product Commission Table
  const productColumns = [
    {
      title: 'Tier',
      dataIndex: 'tier',
      render: (tier) => (
        <Tag style={{
          background: TIER_COLORS[tier] + '20',
          color: TIER_COLORS[tier],
          border: '0.5px solid ' + TIER_COLORS[tier] + '50',
          borderRadius: 20, fontSize: 12,
        }}>
          {tier.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </Tag>
      ),
    },
    {
      title: 'Commission Rate',
      render: (_, r) => (
        <span style={{ color: '#C9A84C', fontWeight: 600, fontSize: 18 }}>
          {config.products[r.tier].rate}%
        </span>
      ),
    },
    {
      title: 'Min Rate',
      render: (_, r) => <span style={{ color: '#7A8A96' }}>{config.products[r.tier].min}%</span>,
    },
    {
      title: 'Max Rate',
      render: (_, r) => <span style={{ color: '#7A8A96' }}>{config.products[r.tier].max}%</span>,
    },
    {
      title: 'Action',
      render: (_, r) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          style={{ color: '#C9A84C' }}
          onClick={() => openEdit('product_tier', r.tier, config.products[r.tier])}
        >
          Edit
        </Button>
      ),
    },
  ];

  const productData = ['economy', 'premium', 'luxury', 'super_luxury']
    .map(tier => ({ tier, key: tier }));

  // Lead Price Table
  const leadColumns = [
    {
      title: 'Job Category',
      dataIndex: 'category',
      render: (cat) => <span style={{ color: '#D4DCE3', textTransform: 'capitalize' }}>{cat}</span>,
    },
    {
      title: 'Lead Price',
      dataIndex: 'price',
      render: (price) => (
        <span style={{ color: '#C9A84C', fontWeight: 600, fontSize: 16 }}>₹{price}</span>
      ),
    },
    {
      title: 'Per',
      render: () => <span style={{ color: '#7A8A96' }}>per contractor</span>,
    },
    {
      title: 'Action',
      render: (_, r) => (
        <Button type="text" icon={<EditOutlined />} style={{ color: '#C9A84C' }}
          onClick={() => openEdit('lead', r.category, { price: r.price })}>
          Edit
        </Button>
      ),
    },
  ];

  const leadData = Object.entries(config.leads)
    .map(([cat, price]) => ({ category: cat, price, key: cat }));

  return (
    <div>
      {/* Header Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Avg Product Commission', value: '11%', color: '#C9A84C', desc: 'Across all tiers' },
          { title: 'B2B Commission', value: config.b2b.rate + '%', color: '#1890FF', desc: 'Retailer to Supplier' },
          { title: 'Real Estate Commission', value: config.realestate.rate + '%', color: '#993556', desc: 'Property deal referral' },
          { title: 'Lead Categories', value: '12', color: '#52C41A', desc: 'Job categories active' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: '#7A8A96', marginBottom: 4 }}>{stat.title}</div>
              <div style={{ fontSize: 28, fontWeight: 600, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: 11, color: '#4A4A47', marginTop: 4 }}>{stat.desc}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Warning Banner */}
      <div style={{
        background: 'rgba(250,173,20,0.08)',
        border: '0.5px solid rgba(250,173,20,0.3)',
        borderRadius: 8, padding: '10px 16px',
        marginBottom: 20, fontSize: 13, color: '#FAAD14',
      }}>
        Changes take effect immediately on all apps. Every change is logged in the audit trail.
        Super Admin only can modify commission rates.
      </div>

      {/* Tabs */}
      <Tabs defaultActiveKey="products"
        tabBarStyle={{ borderBottom: '0.5px solid rgba(201,168,76,0.2)' }}>

        {/* Product Commissions */}
        <TabPane tab="Product Commissions" key="products">
          <Card style={{ ...CARD_STYLE, marginTop: 16 }} bodyStyle={{ padding: 0 }}>
            <div style={{
              padding: '12px 16px',
              borderBottom: '0.5px solid rgba(201,168,76,0.2)',
              color: '#C9A84C', fontWeight: 500,
            }}>
              Commission by Product Tier
            </div>
            <Table
              dataSource={productData}
              columns={productColumns}
              pagination={false}
              rowKey="tier"
            />
          </Card>

          {/* B2B and Real Estate */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ color: '#C9A84C', fontWeight: 500 }}>B2B Orders Commission</span>
                  <Button type="text" icon={<EditOutlined />} style={{ color: '#C9A84C' }}
                    onClick={() => openEdit('b2b', 'b2b', config.b2b)}>Edit</Button>
                </div>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic title={<span style={{ color: '#7A8A96', fontSize: 11 }}>Current Rate</span>}
                      value={config.b2b.rate} suffix="%" valueStyle={{ color: '#1890FF', fontSize: 24 }} />
                  </Col>
                  <Col span={8}>
                    <Statistic title={<span style={{ color: '#7A8A96', fontSize: 11 }}>Min</span>}
                      value={config.b2b.min} suffix="%" valueStyle={{ color: '#7A8A96', fontSize: 20 }} />
                  </Col>
                  <Col span={8}>
                    <Statistic title={<span style={{ color: '#7A8A96', fontSize: 11 }}>Max</span>}
                      value={config.b2b.max} suffix="%" valueStyle={{ color: '#7A8A96', fontSize: 20 }} />
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ color: '#C9A84C', fontWeight: 500 }}>Real Estate Commission</span>
                  <Button type="text" icon={<EditOutlined />} style={{ color: '#C9A84C' }}
                    onClick={() => openEdit('realestate', 'realestate', config.realestate)}>Edit</Button>
                </div>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic title={<span style={{ color: '#7A8A96', fontSize: 11 }}>Current Rate</span>}
                      value={config.realestate.rate} suffix="%" valueStyle={{ color: '#993556', fontSize: 24 }} />
                  </Col>
                  <Col span={8}>
                    <Statistic title={<span style={{ color: '#7A8A96', fontSize: 11 }}>Min</span>}
                      value={config.realestate.min} suffix="%" valueStyle={{ color: '#7A8A96', fontSize: 20 }} />
                  </Col>
                  <Col span={8}>
                    <Statistic title={<span style={{ color: '#7A8A96', fontSize: 11 }}>Max</span>}
                      value={config.realestate.max} suffix="%" valueStyle={{ color: '#7A8A96', fontSize: 20 }} />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Lead Pricing */}
        <TabPane tab="Lead Pricing" key="leads">
          <Card style={{ ...CARD_STYLE, marginTop: 16 }} bodyStyle={{ padding: 0 }}>
            <div style={{
              padding: '12px 16px',
              borderBottom: '0.5px solid rgba(201,168,76,0.2)',
              color: '#C9A84C', fontWeight: 500,
            }}>
              Lead Price per Job Category — charged to contractors
            </div>
            <Table
              dataSource={leadData}
              columns={leadColumns}
              pagination={false}
              rowKey="category"
            />
          </Card>
        </TabPane>

        {/* Delivery Fees */}
        <TabPane tab="Delivery Fees" key="delivery">
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {[
              { key: 'light', label: 'Light Goods (Bike/Auto)', icon: '🛵', color: '#52C41A' },
              { key: 'heavy', label: 'Heavy Goods (Pickup Truck)', icon: '🚚', color: '#1890FF' },
              { key: 'truck', label: 'Bulk/Industrial (Full Truck)', icon: '🏗️', color: '#722ED1' },
            ].map(item => (
              <Col xs={24} lg={8} key={item.key}>
                <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <span style={{ fontSize: 20, marginRight: 8 }}>{item.icon}</span>
                      <span style={{ color: item.color, fontWeight: 500 }}>{item.label}</span>
                    </div>
                    <Button type="text" icon={<EditOutlined />} style={{ color: '#C9A84C' }}
                      onClick={() => openEdit('delivery', item.key, delivery[item.key])}>Edit</Button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#7A8A96', fontSize: 13 }}>Base fee</span>
                      <span style={{ color: '#C9A84C', fontWeight: 500 }}>₹{delivery[item.key].base}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#7A8A96', fontSize: 13 }}>Per km rate</span>
                      <span style={{ color: '#C9A84C', fontWeight: 500 }}>₹{delivery[item.key].perKm}/km</span>
                    </div>
                    {delivery[item.key].freeAbove && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#7A8A96', fontSize: 13 }}>Free delivery above</span>
                        <span style={{ color: '#52C41A', fontWeight: 500 }}>₹{delivery[item.key].freeAbove}</span>
                      </div>
                    )}
                    {delivery[item.key].weightSurcharge && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#7A8A96', fontSize: 13 }}>Weight surcharge</span>
                        <span style={{ color: '#FAAD14', fontWeight: 500 }}>₹{delivery[item.key].weightSurcharge}/quintal</span>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </TabPane>

        {/* Subscription Plans */}
        <TabPane tab="Subscription Plans" key="plans">
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {plans.map(plan => (
              <Col xs={24} lg={12} key={plan.id}>
                <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div>
                      <span style={{ color: '#D4DCE3', fontWeight: 500, fontSize: 14 }}>{plan.name}</span>
                      {plan.featured && (
                        <Tag style={{ marginLeft: 8, background: 'rgba(201,168,76,0.15)', color: '#C9A84C', border: 'none', borderRadius: 20, fontSize: 10 }}>
                          Featured
                        </Tag>
                      )}
                    </div>
                    <span style={{ color: '#C9A84C', fontWeight: 600, fontSize: 20 }}>
                      {plan.price === 0 ? 'Free' : '₹' + plan.price + '/mo'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <Tag style={{ background: 'rgba(24,144,255,0.1)', color: '#1890FF', border: 'none', borderRadius: 20 }}>
                      {plan.products === 999 ? 'Unlimited' : plan.products} products
                    </Tag>
                    <Tag style={{
                      background: plan.analytics ? 'rgba(82,196,26,0.1)' : 'rgba(100,100,100,0.1)',
                      color: plan.analytics ? '#52C41A' : '#666',
                      border: 'none', borderRadius: 20,
                    }}>
                      {plan.analytics ? 'Analytics included' : 'No analytics'}
                    </Tag>
                    <Tag style={{
                      background: plan.featured ? 'rgba(201,168,76,0.1)' : 'rgba(100,100,100,0.1)',
                      color: plan.featured ? '#C9A84C' : '#666',
                      border: 'none', borderRadius: 20,
                    }}>
                      {plan.featured ? 'Featured badge' : 'No featured badge'}
                    </Tag>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </TabPane>

      </Tabs>

      {/* Edit Modal */}
      <Modal
        title={<span style={{ color: '#C9A84C' }}>Edit Configuration</span>}
        open={editModal}
        onCancel={() => setEditModal(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditModal(false)}>Cancel</Button>
            <Button
              loading={loading}
              icon={<SaveOutlined />}
              onClick={saveEdit}
              style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
            >
              Save Changes
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 16, padding: 12, background: 'rgba(250,173,20,0.08)', borderRadius: 8, fontSize: 12, color: '#FAAD14' }}>
          This change takes effect immediately and is logged in the audit trail.
        </div>

        {(editType === 'product_tier' || editType === 'b2b' || editType === 'realestate') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ color: '#7A8A96', fontSize: 12, marginBottom: 6 }}>Commission Rate (%)</div>
              <InputNumber
                value={tempValue.rate}
                min={0} max={50} step={0.5}
                onChange={val => setTempValue(prev => ({ ...prev, rate: val }))}
                style={{ width: '100%', background: '#222', borderColor: 'rgba(201,168,76,0.3)', color: '#D4DCE3' }}
                size="large"
              />
            </div>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ color: '#7A8A96', fontSize: 12, marginBottom: 6 }}>Minimum (%)</div>
                <InputNumber
                  value={tempValue.min} min={0} max={50}
                  onChange={val => setTempValue(prev => ({ ...prev, min: val }))}
                  style={{ width: '100%' }} />
              </Col>
              <Col span={12}>
                <div style={{ color: '#7A8A96', fontSize: 12, marginBottom: 6 }}>Maximum (%)</div>
                <InputNumber
                  value={tempValue.max} min={0} max={50}
                  onChange={val => setTempValue(prev => ({ ...prev, max: val }))}
                  style={{ width: '100%' }} />
              </Col>
            </Row>
          </div>
        )}

        {editType === 'lead' && (
          <div>
            <div style={{ color: '#7A8A96', fontSize: 12, marginBottom: 6 }}>Lead Price (₹) per contractor</div>
            <InputNumber
              value={tempValue.price}
              min={0} max={10000} step={50}
              prefix="₹"
              onChange={val => setTempValue({ price: val })}
              style={{ width: '100%', background: '#222', borderColor: 'rgba(201,168,76,0.3)' }}
              size="large"
            />
          </div>
        )}

        {editType === 'delivery' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ color: '#7A8A96', fontSize: 12, marginBottom: 6 }}>Base Fee (₹)</div>
              <InputNumber value={tempValue.base} min={0}
                onChange={val => setTempValue(prev => ({ ...prev, base: val }))}
                style={{ width: '100%' }} size="large" />
            </div>
            <div>
              <div style={{ color: '#7A8A96', fontSize: 12, marginBottom: 6 }}>Per KM Rate (₹)</div>
              <InputNumber value={tempValue.perKm} min={0}
                onChange={val => setTempValue(prev => ({ ...prev, perKm: val }))}
                style={{ width: '100%' }} size="large" />
            </div>
            {tempValue.freeAbove !== undefined && (
              <div>
                <div style={{ color: '#7A8A96', fontSize: 12, marginBottom: 6 }}>Free Delivery Above (₹)</div>
                <InputNumber value={tempValue.freeAbove} min={0}
                  onChange={val => setTempValue(prev => ({ ...prev, freeAbove: val }))}
                  style={{ width: '100%' }} size="large" />
              </div>
            )}
            {tempValue.weightSurcharge !== undefined && (
              <div>
                <div style={{ color: '#7A8A96', fontSize: 12, marginBottom: 6 }}>Weight Surcharge (₹/quintal)</div>
                <InputNumber value={tempValue.weightSurcharge} min={0}
                  onChange={val => setTempValue(prev => ({ ...prev, weightSurcharge: val }))}
                  style={{ width: '100%' }} size="large" />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}